import { canonicalDepartmentName } from './rbac'

// Timesheets: what a row looks like for each department.
//
// A timesheet row is one piece of work on one day. Every row has a date, a duration, a category and a
// note. A row in the "work" category is the person's own department work: it takes that department's
// activity list and the links that make sense for it (a project for Development, a lead or deal for
// Sales, a filing period for Accounts, ...). The other categories (internal, training, admin, meeting)
// need no links, and leave and holiday rows are created by the system.

export const TIMESHEET_CATEGORIES = ['work', 'internal', 'training', 'admin', 'meeting', 'leave', 'holiday'] as const
export type TimesheetCategory = (typeof TIMESHEET_CATEGORIES)[number]

/** Categories a person can add by hand. Leave and holiday rows come from the leave and holiday records. */
export const MANUAL_TIMESHEET_CATEGORIES: TimesheetCategory[] = ['work', 'internal', 'training', 'admin', 'meeting']

export const CATEGORY_LABEL: Record<TimesheetCategory, string> = {
  work: 'Work',
  internal: 'Internal',
  training: 'Training',
  admin: 'Admin',
  meeting: 'Meeting',
  leave: 'Leave',
  holiday: 'Holiday',
}

export type TimesheetLinkField = 'project' | 'task' | 'lead' | 'deal' | 'campaign'
export type TimesheetExtraField = 'channel' | 'deliverable' | 'revisionRound' | 'filingPeriod'

export interface TimesheetActivity {
  key: string
  label: string
}

export interface DepartmentTimesheetConfig {
  /** Canonical department name this applies to. */
  department: string
  /** Links a "work" row can carry; the required ones must be present. */
  requiredLinks: TimesheetLinkField[]
  /** One of these must be present when listed (e.g. a lead or a deal). */
  oneOfLinks: TimesheetLinkField[]
  optionalLinks: TimesheetLinkField[]
  extraFields: TimesheetExtraField[]
  activities: TimesheetActivity[]
}

export const TIMESHEET_CHANNELS = ['Paid ads', 'SEO', 'Social', 'Email', 'Content', 'Other'] as const

const acts = (...labels: string[]): TimesheetActivity[] =>
  labels.map((label) => ({ key: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''), label }))

const NO_LINKS = { requiredLinks: [] as TimesheetLinkField[], oneOfLinks: [] as TimesheetLinkField[], optionalLinks: [] as TimesheetLinkField[] }

const CONFIGS: DepartmentTimesheetConfig[] = [
  {
    department: 'Development',
    requiredLinks: ['project'],
    oneOfLinks: [],
    optionalLinks: ['task'],
    extraFields: [],
    activities: acts('Development', 'Code review', 'QA / Testing', 'Meeting', 'Support / Bug-fix', 'Deployment / DevOps', 'Research'),
  },
  {
    department: 'Creative & Marketing',
    requiredLinks: [],
    oneOfLinks: ['project', 'campaign'],
    optionalLinks: ['task'],
    extraFields: ['deliverable', 'revisionRound', 'channel'],
    activities: acts(
      'Discovery',
      'Concept',
      'Design',
      'Production files',
      'Revision',
      'Client review',
      'Project management',
      'Strategy',
      'Content creation',
      'Paid ads',
      'SEO',
      'Social',
      'Email',
      'Analytics / reporting',
      'Coordination'
    ),
  },
  {
    department: 'Sales',
    requiredLinks: [],
    oneOfLinks: [],
    optionalLinks: ['project', 'task', 'lead', 'deal'],
    extraFields: [],
    activities: acts('Call', 'Meeting', 'Demo', 'Follow-up', 'Proposal / Quotation', 'Negotiation', 'Travel', 'Prospecting'),
  },
  {
    department: 'HR',
    ...NO_LINKS,
    extraFields: [],
    activities: acts('Recruitment', 'Onboarding', 'Payroll', 'Employee relations', 'Policy & compliance', 'Training admin'),
  },
  {
    department: 'Accounts',
    ...NO_LINKS,
    extraFields: ['filingPeriod'],
    activities: acts('Invoicing', 'Receivables / Payables', 'Reconciliation', 'GST filing', 'TDS filing', 'Payroll', 'Audit', 'Month-end close'),
  },
  {
    department: 'Superadmin',
    ...NO_LINKS,
    extraFields: [],
    activities: acts('Management', 'Review', 'Strategy', 'Meeting'),
  },
]

/** Used for a user whose department is blank or not one of the known ones. */
const GENERAL: DepartmentTimesheetConfig = {
  department: 'General',
  ...NO_LINKS,
  extraFields: [],
  activities: acts('Work', 'Meeting', 'Planning', 'Support'),
}

/** The row configuration for a department (matched without regard to case); unknown departments get a general one. */
export function timesheetConfigFor(department?: string | null): DepartmentTimesheetConfig {
  const wanted = String(canonicalDepartmentName(department) ?? '').trim().toLowerCase()
  return CONFIGS.find((c) => c.department.toLowerCase() === wanted) ?? GENERAL
}

export const TIMESHEET_DEPARTMENTS = CONFIGS.map((c) => c.department)

/** The most a day can hold, in minutes. */
export const MAX_DAY_MINUTES = 24 * 60

/** The shape of a row for validation; this is what the API accepts and what is stored. */
export interface TimesheetRowInput {
  date: string
  durationMinutes: number
  category: TimesheetCategory
  activityType?: string | null
  projectId?: string | null
  taskId?: string | null
  leadId?: string | null
  dealId?: string | null
  campaign?: string | null
  channel?: string | null
  deliverable?: string | null
  revisionRound?: number | null
  filingPeriod?: string | null
  note?: string | null
}

const present = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== ''

const LINK_VALUE: Record<TimesheetLinkField, (r: TimesheetRowInput) => unknown> = {
  project: (r) => r.projectId,
  task: (r) => r.taskId,
  lead: (r) => r.leadId,
  deal: (r) => r.dealId,
  campaign: (r) => r.campaign,
}
const LINK_LABEL: Record<TimesheetLinkField, string> = { project: 'a project', task: 'a task', lead: 'a lead', deal: 'a deal', campaign: 'a campaign' }

/** What is wrong with a row a person entered, in plain words. An empty list means it is fine. */
export function validateTimesheetRow(department: string | null | undefined, row: TimesheetRowInput): string[] {
  const errors: string[] = []
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(row.date ?? ''))) errors.push('Choose a date.')
  if (!Number.isFinite(row.durationMinutes) || row.durationMinutes < 1) errors.push('Enter the time spent.')
  else if (row.durationMinutes > MAX_DAY_MINUTES) errors.push('A single entry cannot be more than 24 hours.')

  if (!MANUAL_TIMESHEET_CATEGORIES.includes(row.category)) {
    errors.push('Leave and holiday entries are added automatically.')
    return errors
  }

  const config = timesheetConfigFor(department)
  if (row.category === 'work') {
    if (!present(row.activityType)) errors.push('Choose what kind of work it was.')
    else if (!config.activities.some((a) => a.key === row.activityType)) errors.push('Choose an activity from the list.')

    for (const link of config.requiredLinks) {
      if (!present(LINK_VALUE[link](row))) errors.push(`Choose ${LINK_LABEL[link]}.`)
    }
    if (config.oneOfLinks.length > 0 && !config.oneOfLinks.some((l) => present(LINK_VALUE[l](row)))) {
      errors.push(`Choose ${config.oneOfLinks.map((l) => LINK_LABEL[l]).join(' or ')}.`)
    }
    if (present(row.channel) && !(TIMESHEET_CHANNELS as readonly string[]).includes(String(row.channel))) {
      errors.push('Choose a channel from the list.')
    }
    if (present(row.filingPeriod) && !/^\d{4}-(0[1-9]|1[0-2])$/.test(String(row.filingPeriod))) {
      errors.push('Enter the filing period as year and month (for example 2026-10).')
    }
  } else if (!present(row.note)) {
    errors.push('Add a short note saying what it was.')
  }
  return errors
}

/** Minutes as "7h 30m" for messages and reports. */
export function formatMinutes(minutes: number): string {
  const total = Math.max(0, Math.round(minutes))
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m}m`
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}
